import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError } from "../../helpers";
import { getAllGoodsReceiptNotes, cancelGoodsReceiptNote, deleteGoodsReceiptNote } from "../../services/GoodsReceiptNoteServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildGoodsReceiptNoteQueryParams } from "../../utils/listViewDateRanges";
import { fetchAndShowGrnPdf } from "../../utils/grnPdf";
import moment from 'moment';

function GoodsReceiptNoteTable() {
    const InitialData = { records: [] };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ['grn_no'];
    const right_pinned = ['id'];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const summaryTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.goods_receipt_note_date === todayDateLabel
        );
        return {
            total_count: data.records.length,
            today_count: todayRecords.length,
            received_qty: data.records.reduce((sum, row) => sum + (Number(row.received_qty) || 0), 0),
            dc_qty: data.records.reduce((sum, row) => sum + (Number(row.dc_qty) || 0), 0),
        };
    }, [data.records, todayDateLabel]);

    const todayGrnCard = useMemo(() => ({
        label: 'Today GRN',
        value: String(summaryTotals.today_count),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [summaryTotals.today_count]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total GRN',
            value: String(summaryTotals.total_count),
            gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
            shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
        },
        {
            label: 'Received Qty',
            value: Number(summaryTotals.received_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
            shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
        },
        {
            label: 'DC Qty',
            value: Number(summaryTotals.dc_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
            shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant', headerName: 'Plant', size: 65 },
            { fieldName: 'grn_no', headerName: 'GRN No', size: 88 },
            { fieldName: 'goods_receipt_note_date', headerName: 'Date', size: 86 },
            { fieldName: 'vendor_name', headerName: 'Vendor', size: 120 },
            { fieldName: 'product_name', headerName: 'Material', size: 100 },
            { fieldName: 'unit', headerName: 'Unit', size: 55 },
            { fieldName: 'dc_qty', headerName: 'DC Qty', size: 72, align: 'right' },
            { fieldName: 'dc_no', headerName: 'DC No', size: 80 },
            { fieldName: 'dc_dt', headerName: 'DC Date', size: 86 },
            { fieldName: 'vehicle', headerName: 'Vehicle', size: 90 },
            { fieldName: 'invoice_no', headerName: 'Invoice No', size: 90 },
            { fieldName: 'invoice_date', headerName: 'Inv Date', size: 86 },
            { fieldName: 'gross_weight', headerName: 'Gross', size: 68, align: 'right' },
            { fieldName: 'tare_weight', headerName: 'Tare', size: 68, align: 'right' },
            { fieldName: 'received_qty', headerName: 'Rcvd Qty', size: 72, align: 'right' },
            { fieldName: 'difference_qty', headerName: 'Diff Qty', size: 72, align: 'right' },
            { fieldName: 'deduction_qty', headerName: 'Ded Qty', size: 72, align: 'right' },
            { fieldName: 'weighment_name', headerName: 'Weighment', size: 95 },
            { fieldName: 'weighment_slip_no', headerName: 'W Slip No', size: 85 },
            { fieldName: 'status', headerName: 'Status', size: 78 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const loadGoodsReceiptNotes = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllGoodsReceiptNotes(cookies, buildGoodsReceiptNoteQueryParams(config))
            .then((response) => {
                const tableData = (response.goods_receipt_note_list || []).map((grn) => {
                    const isActive = grn.status === true || grn.status === 1;
                    return {
                        id: grn.id,
                        grn_no: `${grn.prefix || ''}${grn.goods_receipt_note_no || ''}`,
                        goods_receipt_note_date: grn.goods_receipt_note_date,
                        gross_weight: grn.gross_weight,
                        tare_weight: grn.tare_weight,
                        net_weight: grn.net_weight,
                        dc_qty: grn.dc_qty,
                        deduction_qty: grn.deduction_qty,
                        received_qty: grn.received_qty,
                        accepted_qty: grn.accepted_qty,
                        dc_no: grn.dc_no,
                        dc_dt: grn.dc_dt,
                        vendor_name: grn.vendor?.name ?? '',
                        product_name: grn.product?.name ?? '',
                        unit: grn.product?.unit?.symbol ?? '',
                        vehicle: grn.transport_mode === 'own' ? grn.vehicle?.name : grn.vendor_vehicle_no,
                        invoice_no: grn.invoice?.invoice_no ?? '',
                        invoice_date: grn.invoice?.invoice_date ?? '',
                        weighment_name: grn.weighment_name,
                        weighment_slip_no: grn.weighment_slip_no,
                        difference_qty: grn.difference_qty,
                        plant: grn.plant?.alias_name ?? '',
                        status: isActive ? 'Active' : 'Cancelled',
                        is_active: isActive,
                        hide_row_edit: isActive ? 'No' : 'Yes',
                        hide_row_cancel: isActive ? 'No' : 'Yes',
                        hide_row_delete: isActive ? 'Yes' : 'No',
                        hide_row_print: 'No',
                    };
                });
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Load Failed');
                setData({ records: [] });
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadGoodsReceiptNotes(config);
    }, [loadGoodsReceiptNotes]);

    const onEditGoodsReceiptNote = (grnId) => {
        navigate(`/GoodsReceiptNoteEdit/${grnId}/`);
    };

    const onCancelGoodsReceiptNote = (grnId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Cancel goods receipt note?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setIsLoading(true);
            cancelGoodsReceiptNote(cookies, grnId)
                .then((response) => {
                    Swal.fire('Cancelled', response?.data?.message || 'Goods receipt note cancelled.', 'success');
                    setRefreshKey((oldKey) => oldKey + 1);
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Cancel Failed');
                    setIsLoading(false);
                });
        });
    };

    const onDeleteGoodsReceiptNote = (grnId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Delete cancelled goods receipt note?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setIsLoading(true);
            deleteGoodsReceiptNote(cookies, grnId)
                .then((response) => {
                    Swal.fire('Deleted', response?.data?.message || 'Goods receipt note deleted.', 'success');
                    setRefreshKey((oldKey) => oldKey + 1);
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Delete Failed');
                    setIsLoading(false);
                });
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((grn) => ({
                plant: grn.plant,
                grn_no: grn.grn_no,
                goods_receipt_note_date: grn.goods_receipt_note_date,
                vendor_name: grn.vendor_name,
                product_name: grn.product_name,
                unit: grn.unit,
                dc_qty: grn.dc_qty,
                dc_no: grn.dc_no,
                dc_dt: grn.dc_dt,
                vehicle: grn.vehicle,
                invoice_no: grn.invoice_no,
                invoice_date: grn.invoice_date,
                gross_weight: grn.gross_weight,
                tare_weight: grn.tare_weight,
                received_qty: grn.received_qty,
                difference_qty: grn.difference_qty,
                deduction_qty: grn.deduction_qty,
                weighment_name: grn.weighment_name,
                weighment_slip_no: grn.weighment_slip_no,
                status: grn.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Goods Receipt Note',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'GRN.pdf',
        );
    };

    const onPrintGoodsReceiptNote = (grnId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchAndShowGrnPdf(cookies, grnId, {
            title: 'Goods Receipt Note',
            downloadFileName: `GRN-${grnId}.pdf`,
        }).finally(() => setIsLoading(false));
    };

    useEffect(() => {
        getListViewSettings(cookies, 'goods_receipt_note')
            .then((response) => {
                loadGoodsReceiptNotes(response.config || {});
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadGoodsReceiptNotes, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/GoodsReceiptNote"
                    addNewLabel="New GRN"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                    <PremiumPageHeader
                        module="STORE"
                        title="List of Goods Receipt Note"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="goods_receipt_note"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todayGrnCard.gradient,
                                boxShadow: todayGrnCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todayGrnCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todayGrnCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF GOODS RECEIPT NOTE"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditGoodsReceiptNote}
                            onRowCancel={onCancelGoodsReceiptNote}
                            onRowDelete={onDeleteGoodsReceiptNote}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintGoodsReceiptNote}
                            hidePrint={false}
                            autoPinActionColumn={false}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: 'relative', right: '40px' }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate('/GoodsReceiptNote'); }}
            >
                Back
            </button>
        </div>
    );
}

export default GoodsReceiptNoteTable;
